/** accumulateByGroup - accumulate entities by group
 * @param entity - entity to accumulate
 * @param accumulatorFunction - function used to accumulate entities
 * @param groupBy - optionally group entities by a key e.g. 'transactionDateTime' and an optional value modifier e.g. format(value, 'yyyy-MM')
 * @param useValueFromPreviousGroup - use value from previous group if no value is found for current group
 * @param returnAsObject - return groups as object
 * @returns Observable<Array<R>> - array of accumulated entities
 */
import { QueryFilterGroupBy } from '../../shared/state/query-filter.functions';
import { get } from 'dot-prop';
import { isArray } from '@datorama/akita';

// TODO: fix return type (will differ depending on groupBy and returnAsObject properties)
export const accumulateByGroup = <T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy
    ) => R,
    groupBy?: QueryFilterGroupBy | Array<QueryFilterGroupBy>,
    useValueFromPreviousGroup = false,
    returnAsObject = true
) => {
    if (!entities) {
        return [];
        //throw new Error('No entities provided');
    }
    if (groupBy && !isArray(groupBy)) {
        // create placeholder for groups
        const GROUPS: { [group: string | number]: R & { entities?: Array<T> } } = {};
        for (let i = 0; i < entities.length; i++) {
            const ENTITY = entities[i];
            let previousGroup;
            let group = get<string | number>(entities[i], groupBy.key);
            // if groupBy.valueModifier is provided...
            if (groupBy.valueModifier) {
                // ...use it to modify the group value
                group = groupBy.valueModifier(group, entities[i]);
            }

            if (useValueFromPreviousGroup && Object.keys(GROUPS).length > 0) {
                previousGroup = get<string | number>(entities[i - 1], groupBy.key);
                if (groupBy.valueModifier && previousGroup) {
                    previousGroup = groupBy.valueModifier(previousGroup, entities[i - 1]);
                }
            }
            // add group and entities
            GROUPS[group] = {
                ...accumulatorFunction(
                    ENTITY,
                    GROUPS[group] ||
                        (useValueFromPreviousGroup ? GROUPS[previousGroup] : undefined),
                    i,
                    groupBy.subgroup
                ),
                entities: [...(GROUPS[group]?.entities || []), ENTITY]
            };
        }
        if (groupBy.subgroup) {
            Object.entries(GROUPS).forEach(([key, value]) => {
                const subgroups = accumulateByGroup(
                    value.entities,
                    accumulatorFunction,
                    groupBy.subgroup
                );
                GROUPS[key] = { ...GROUPS[key], subgroups };
            });
        }
        if (returnAsObject === true) {
            return GROUPS;
        } else if (returnAsObject === false) {
            return Object.values(GROUPS) as Array<R>;
        }
    } else if (groupBy && isArray(groupBy)) {
        // create placeholder for groups
        const GROUPS: { [group: string | number]: R } = groupBy.reduce(
            (acc, curr) => ({ ...acc, [curr.name]: {} }),
            {}
        );
        for (let i = 0; i < entities.length; i++) {
            const ENTITY = entities[i];
            groupBy.forEach((group) => {
                let groupValue = get<string | number>(entities[i], group.key);
                // if groupBy.valueModifier is provided...
                if (group.valueModifier) {
                    // ...use it to modify the group value
                    groupValue = group.valueModifier(groupValue, entities[i]);
                }
                // add group
                GROUPS[group.name][groupValue] = accumulatorFunction(
                    ENTITY,
                    GROUPS[group.name][groupValue],
                    i,
                    group.subgroup
                );
            });
        }

        groupBy
            .filter((group) => group.subgroup)
            .forEach((group) => {
                Object.entries(GROUPS[group.name]).forEach(([key, entities]) => {
                    const subgroups = accumulateByGroup(
                        entities,
                        accumulatorFunction,
                        group.subgroup
                    );
                    GROUPS[group.name][key] = {
                        ...GROUPS[group.name][key],
                        subgroups,
                        entities
                    };
                });
            });
        if (returnAsObject) {
            return GROUPS;
        } else {
            return Object.values(GROUPS) as Array<R>;
        }
    } else {
        let accumulated: R;
        for (let i = 0; i < entities.length; i++) {
            const ENTITY = entities[i];
            accumulated = accumulatorFunction(ENTITY, accumulated, i);
        }
        return accumulated;
    }
};
